/** Moedas UCC: tipos e chamadas da loja, da carteira do time e do painel. */

import { api } from './api';

export interface ItemDaLoja {
  id: string;
  nome: string;
  descricao: string | null;
  preco: number;
  imagemUrl: string | null;
  estoque: number | null;
}

export interface Pacote {
  id: string;
  moedas: number;
  precoCentavos: number;
}

export interface TabelaDeMoedas {
  campeao: number;
  vice: number;
  terceiro: number;
  artilheiro: number;
  assistencias: number;
  timesDeReferencia: number;
}

export interface Lancamento {
  id: string;
  valor: number;
  tipo: 'premio' | 'compra' | 'resgate' | 'estorno' | 'ajuste' | 'expiracao' | 'reembolso';
  origem: 'ganha' | 'comprada';
  descricao: string;
  createdAt: number;
}

export interface Carteira {
  time: { id: string; name: string; shortName: string; crestUrl: string | null };
  saldo: { total: number; ganhas: number; compradas: number };
  lancamentos: Lancamento[];
  resgates: Array<{ id: string; item: string; preco: number; status: 'pendente' | 'entregue' | 'cancelado'; entrega: string | null; observacao: string | null; createdAt: number }>;
  compras: Array<{ id: string; moedas: number; valorCentavos: number; status: 'aguardando' | 'paga' | 'cancelada' | 'reembolsada'; pixPayload: string | null; txid: string; createdAt: number }>;
}

export interface PainelDeMoedas {
  resgates: Array<{ id: string; status: string; preco: number; entrega: string | null; observacao: string | null; createdAt: number; item: string; teamId: string; time: string }>;
  compras: Array<{ id: string; status: string; moedas: number; valorCentavos: number; txid: string; createdAt: number; time: string }>;
  itens: Array<ItemDaLoja & { ativo: boolean; ordem: number }>;
  config: { tabela: TabelaDeMoedas; validadeMeses: number; pix: { chave: string | null; nome: string | null; cidade: string | null } };
  pacotes: Pacote[];
  times: Array<{ id: string; name: string; saldo: number }>;
}

export const moedasApi = {
  loja: () => api<{ itens: ItemDaLoja[]; pacotes: Pacote[]; tabela: TabelaDeMoedas; vendaAtiva: boolean }>('/moedas/loja'),
  meusTimes: () => api<{ times: Array<{ id: string; name: string; shortName: string; crestUrl: string | null; saldo: number }> }>('/moedas/meus-times'),
  carteira: (teamId: string) => api<Carteira>(`/moedas/times/${teamId}`),
  resgatar: (teamId: string, itemId: string) =>
    api<{ id: string; item: string; preco: number }>(`/moedas/times/${teamId}/resgates`, { method: 'POST', body: JSON.stringify({ itemId }) }),
  comprar: (teamId: string, pacote: string) =>
    api<{ id: string; moedas: number; valorCentavos: number; pixPayload: string; txid: string }>(`/moedas/times/${teamId}/compras`, {
      method: 'POST',
      body: JSON.stringify({ pacote }),
    }),
  avisarPagamento: (teamId: string, compraId: string) =>
    api<{ ok: true }>(`/moedas/times/${teamId}/compras/${compraId}/paguei`, { method: 'POST' }),
};

export const moedasAdminApi = {
  painel: () => api<PainelDeMoedas>('/admin/moedas'),
  salvarConfig: (c: TabelaDeMoedas & { validadeMeses: number; pixChave: string; pixNome: string; pixCidade: string }) =>
    api<{ ok: true }>('/admin/moedas/config', { method: 'POST', body: JSON.stringify(c) }),
  salvarItem: (i: Partial<ItemDaLoja> & { nome: string; preco: number; ativo: boolean; ordem?: number }) =>
    api<{ ok: true }>('/admin/moedas/itens', { method: 'POST', body: JSON.stringify(i) }),
  enviarImagem: (arquivo: File) => {
    const form = new FormData();
    form.set('file', arquivo, arquivo.name);
    return api<{ imagemUrl: string }>('/admin/moedas/itens/imagem', { method: 'POST', body: form });
  },
  entregar: (id: string, entrega: string) =>
    api<{ ok: true }>(`/admin/moedas/resgates/${id}/entregar`, { method: 'POST', body: JSON.stringify({ entrega }) }),
  cancelarResgate: (id: string, motivo: string) =>
    api<{ ok: true }>(`/admin/moedas/resgates/${id}/cancelar`, { method: 'POST', body: JSON.stringify({ motivo }) }),
  confirmarCompra: (id: string) => api<{ ok: true }>(`/admin/moedas/compras/${id}/confirmar`, { method: 'POST' }),
  cancelarCompra: (id: string) => api<{ ok: true }>(`/admin/moedas/compras/${id}/cancelar`, { method: 'POST' }),
  reembolsarCompra: (id: string, motivo: string) =>
    api<{ ok: true }>(`/admin/moedas/compras/${id}/reembolsar`, { method: 'POST', body: JSON.stringify({ motivo }) }),
  ajustar: (teamId: string, valor: number, motivo: string) =>
    api<{ ok: true }>('/admin/moedas/ajustes', { method: 'POST', body: JSON.stringify({ teamId, valor, motivo }) }),
  previaDaEdicao: (tournamentId: string) =>
    api<{ encerrada: boolean; premios: Array<{ teamId: string; time: string; rotulo: string; moedas: number; creditado: boolean; referencia: string }> }>(
      `/admin/moedas/torneios/${tournamentId}`,
    ),
  creditarEdicao: (tournamentId: string) =>
    api<{ ok: true; creditados: number; estornados: number }>(`/admin/moedas/torneios/${tournamentId}/creditar`, { method: 'POST' }),
};

/** "1.150 moedas" -- sempre com separador de milhar. */
export const moedas = (n: number) => `${n.toLocaleString('pt-BR')} ${Math.abs(n) === 1 ? 'moeda' : 'moedas'}`;
